
import { CoinChip, Badge } from '../ui/components';
import { state, updateCoins, isPremiumActive } from '../store';
import { api, isHalted } from '../api/client';
import { t, getLocalized } from '../i18n';
import { sanitizeHtml } from '../utils/sanitize';

let activeGameId: string = 'tod';
let _unlockInProgress = false;

const _loadingGames = new Set<string>();

function applyOptimisticUnlock(gameId: string, levelId: string): void {
  // Unlocking a whole game from Home uses a synthetic level id.
  if (levelId === '__game__') {
    const game = state.games.find((g: any) => g.id === gameId);
    if (game) game.user_has_access = true;
    return;
  }

  const level = state.levels[gameId]?.find((l) => l.id === levelId);
  if (level) level.user_has_access = true;
}

async function refreshAccessState(gameId: string, levelId: string): Promise<void> {
  // Always refresh the current game's levels after unlock.
  delete state.levels[gameId];
  await ensureLevelsLoaded(gameId);

  // Whole-game unlock updates Home card access state.
  if (levelId === '__game__') {
    const gm = state.games.find((g: any) => g.id === gameId);
    if (gm) gm.user_has_access = true;
  }
}

async function ensureLevelsLoaded(gameId: string) {
  if (isHalted()) return;  // terminal auth state — no fetches
  if (gameId === 'activity') return;
  if (state.levels[gameId] || _loadingGames.has(gameId)) return;
  _loadingGames.add(gameId);
  try {
    const res = await api.getLevels(gameId);
    state.levels[gameId] = (res as any).levels ?? res;
  } catch(e) {
    console.error('Failed to load levels', e);
    state.levels[gameId] = [];
  } finally {
    _loadingGames.delete(gameId);
  }
}

declare global {
  interface Window {
    selectGameTab: (gameId: string) => void;
    showUnlockPopup: (name: string, icon: string, cost: number, levelId: string, gameId: string) => void;
    navTo: (screen: string) => void;
  }
}

window.selectGameTab = (gameId: string) => {
  activeGameId = gameId;
  const tabBtn = document.querySelector(`.game-tab[data-game="${gameId}"]`);
  if (tabBtn) {
    tabBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    // Only re-render if already on LevelSelect screen (tab exists in DOM)
    if (typeof window.renderApp === 'function') {
      window.renderApp();
    }
  }
  // If tab not in DOM — called from Home.ts; navTo('games') will render LevelSelect
};

window.showUnlockPopup = (levelName: string, levelIcon: string, cost: number, levelId: string, gameId: string) => {
  // Предотвратить открытие popup пока идёт покупка
  if (_unlockInProgress) return;

  // Удалить существующий popup если есть
  document.getElementById('unlock-popup-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'unlock-popup-overlay';
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(0,0,0,0.75); backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
    padding: 24px; animation: fadeIn 0.2s ease;
  `;
  
  overlay.innerHTML = `
    <style>
      @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
      @keyframes popIn { from { transform: scale(0.7); opacity: 0 } to { transform: scale(1); opacity: 1 } }
      @keyframes glowPulse {
        0%, 100% { filter: drop-shadow(0 0 8px rgba(255,215,0,0.3)); }
        50% { filter: drop-shadow(0 0 24px rgba(255,215,0,0.9)); }
      }
    </style>
    <div style="
      background: linear-gradient(135deg, #13112A, #1A1836);
      border: 1px solid rgba(255,215,0,0.3);
      border-radius: 28px; padding: 32px 24px; text-align: center;
      max-width: 320px; width: 100%; position: relative; overflow: hidden;
      animation: popIn 0.3s cubic-bezier(.4,0,.2,1);
      box-shadow: 0 0 60px rgba(255,215,0,0.15);
      display: flex; flex-direction: column; align-items: center;
    " id="unlock-popup-card">
      
      <div style="font-size: 56px; margin-bottom: 12px; animation: iconFloat 3s ease-in-out infinite;">
        <div style="animation: glowPulse 2s ease-in-out infinite;">${sanitizeHtml(levelIcon)}</div>
      </div>
      <div style="
        font-family: 'Inter', sans-serif; font-size: 26px; font-weight: 800;
        background: linear-gradient(135deg, #FFD700, #FF8C00);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        margin-bottom: 6px; text-align: center;
      ">${t('unlock_buy_btn').split(' ')[0]}</div>
      <div style="color: #FFFFFF; font-size: 17px; font-weight: 700; margin-bottom: 4px; text-align: center;">${sanitizeHtml(levelName)}</div>
      <div style="color: #B0ADCF; font-size: 13px; font-weight: 600; margin-bottom: 24px; text-align: center;">
        ${t('unlock_cost_label')} <span style="color:#FFD700; font-weight: 800;">🪙 ${cost}</span>
      </div>
      
      <div style="width: 100%; display: flex; flex-direction: column; gap: 8px;">
        <button id="unlock-pay-btn" style="
          width: 100%; display: block; padding: 16px 24px;
          background: linear-gradient(135deg, #FF2D6B, #A020F0);
          border: none; border-radius: 9999px; color: white;
          font-size: 16px; font-weight: 800; cursor: pointer;
          box-shadow: 0 0 24px rgba(255,45,107,0.55);
        ">${t('unlock_buy_btn').replace('{n}', String(cost))}</button>
        <button data-action="close-unlock-popup" style="
          width: 100%; display: block; padding: 12px 24px;
          background: transparent;
          border: none; color: rgba(255,255,255,0.6);
          font-size: 14px; font-weight: 600; cursor: pointer;
          text-align: center;
        ">${t('cancel_btn')}</button>
      </div>
    </div>
  `;
  
  // Клик на оверлей (не карточку) — закрыть
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  
  document.body.appendChild(overlay);

  const payBtn = overlay.querySelector('#unlock-pay-btn') as HTMLButtonElement | null;
  if (payBtn) {
    payBtn.onclick = () => {
      if (_unlockInProgress) return;
      _unlockInProgress = true;

      const originalText = payBtn.innerHTML;
      payBtn.innerHTML = '⏳...';
      payBtn.style.pointerEvents = 'none';

      api.unlock(gameId, levelId)
        .then(res => {
          try { (window as any).Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success'); } catch(e){}
          
          payBtn.innerHTML = '✅';
          payBtn.style.background = 'linear-gradient(135deg, #00b09b, #96c93d)';
          payBtn.style.boxShadow = '0 0 24px rgba(0, 176, 155, 0.55)';
          payBtn.style.transform = 'scale(1.05)';
          payBtn.style.transition = 'all 0.3s ease';

          const burst = document.createElement('div');
          burst.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:10;display:flex;align-items:center;justify-content:center;';
          burst.innerHTML = `
            <style>
              @keyframes burstOut {
                0% { transform: scale(0.5) translate(0,0); opacity: 1; }
                100% { transform: scale(1.5) translate(var(--tx), var(--ty)); opacity: 0; }
              }
            </style>
            ${Array.from({length: 12}).map((_, i) => {
              const angle = (i * 30) * Math.PI / 180;
              const tx = Math.cos(angle) * 120 + 'px';
              const ty = Math.sin(angle) * 120 + 'px';
              const emoji = ['✨', '🪙', '🌟', '🎉'][i % 4];
              return `<div style="position:absolute;font-size:24px;animation:burstOut 0.8s cubic-bezier(0.1, 0.8, 0.3, 1) forwards;--tx:${tx};--ty:${ty};">${emoji}</div>`;
            }).join('')}
          `;
          
          const card = document.getElementById('unlock-popup-card');
          if (card) card.appendChild(burst);

          updateCoins(res.coins_left);

          // Optimistic UI update: unlock level or whole game immediately.
          applyOptimisticUnlock(gameId, levelId);

          setTimeout(() => {
            _unlockInProgress = false;
            overlay.remove();
            if (typeof window.showToast === 'function') window.showToast(t('unlock_success_title'), 'success');
            if (typeof window.renderApp === 'function') window.renderApp();

            // Re-fetch from server in background after render
            setTimeout(() => {
              refreshAccessState(gameId, levelId)
                .finally(() => {
                  if (typeof window.renderApp === 'function') window.renderApp();
                });
            }, 500);
          }, 800);
        })
        .catch((err) => {
          // 409 = Already unlocked — treat as success
          if (err?.status === 409 || err?.message?.includes('409')) {
            _unlockInProgress = false;
            applyOptimisticUnlock(gameId, levelId);
            overlay.remove();
            if (typeof window.showToast === 'function') window.showToast(t('unlock_success_title'), 'success');
            if (typeof window.renderApp === 'function') window.renderApp();
            refreshAccessState(gameId, levelId)
              .finally(() => {
                if (typeof window.renderApp === 'function') window.renderApp();
              });
            return;
          }
          _unlockInProgress = false;
          payBtn.innerHTML = originalText;
          payBtn.style.pointerEvents = 'auto';
          try { (window as any).Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error'); } catch(e){}
          if (typeof window.showToast === 'function') window.showToast(t('error_not_enough_coins'), 'error');
        });
    };
  }
};

export function setupLevelSelectListener(): void {
  const screen = document.getElementById('screen-games');
  if (!screen) return;
  screen.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-level-action]');
    if (!el) return;
    const action = el.dataset.levelAction;
    if (action === 'open-setup') {
      window.openGameSetup(el.dataset.gameId!, parseInt(el.dataset.levelIndex!, 10));
    } else if (action === 'show-unlock') {
      window.showUnlockPopup(el.dataset.levelName!, el.dataset.levelIcon!, parseInt(el.dataset.levelCost!, 10), el.dataset.levelId!, el.dataset.gameId!);
    } else if (action === 'goto-invite') {
      window.navTo('invite');
    } else if (action === 'goto-premium') {
      window.navTo('premium');
    }
  });
}

export function renderLevelSelect(): string {
  let tabsHtml = '';
  
  if (!state.games || state.games.length === 0) {
    tabsHtml = Array.from({length: 4}).map(() => `
      <div class="flex flex-col items-center gap-[5px] p-[10px_14px] rounded-md border-[1.5px] border-transparent bg-white/5 animate-pulse min-w-[68px] shrink-0 h-[64px]"></div>
    `).join('');
  } else {
    tabsHtml = state.games.filter((game) => game.id !== 'activity').map((game, index) => {
      const isActive = game.id === activeGameId;
      const activeClass = isActive 
        ? 'bg-neon-pink/[0.12] border-neon-pink shadow-[0_0_16px_rgba(255,45,107,0.2)]' 
        : 'bg-glass border-transparent';
      const textClass = isActive ? 'text-neon-pink' : 'text-t-muted';
      const delay = -(index * 0.4) + 's';
          
      return `
        <div 
          class="game-tab flex flex-col items-center gap-[5px] p-[10px_14px] rounded-md border-[1.5px] cursor-pointer transition-all duration-200 min-w-[68px] shrink-0 ${activeClass}" 
          data-action="select-game-tab" data-game="${game.id}"
          data-game="${game.id}"
        >
          <span class="text-[22px] animate-[iconFloat_3s_ease-in-out_infinite]" style="animation-delay: ${delay};">${game.icon}</span>
          <span class="text-[9px] font-extrabold text-center max-w-[64px] leading-[1.2] whitespace-nowrap overflow-hidden text-ellipsis ${textClass}">
            ${sanitizeHtml(getLocalized(game.name))}
          </span>
        </div>
      `;
    }).join('');
  }

  let levelsHtml = '';
  const levels = state.levels[activeGameId];

  if (!levels) {
    ensureLevelsLoaded(activeGameId).then(() => { if (!isHalted() && typeof window.renderApp === 'function') window.renderApp(); });
    // skeleton grey cards
    levelsHtml = Array.from({length: 5}).map(() => `
      <div style="
        display:flex; align-items:center; gap:14px; padding:14px 16px;
        border-radius:18px; background:rgba(255,255,255,0.05);
        animation: pulse 1.5s ease-in-out infinite; height:82px;
      ">
        <div style="width:52px;height:52px;border-radius:12px;background:rgba(255,255,255,0.08);flex-shrink:0;"></div>
        <div style="flex:1;">
          <div style="height:14px;background:rgba(255,255,255,0.1);border-radius:6px;margin-bottom:8px;width:65%;"></div>
          <div style="height:10px;background:rgba(255,255,255,0.07);border-radius:6px;width:80%;margin-bottom:8px;"></div>
          <div style="height:18px;background:rgba(255,255,255,0.06);border-radius:20px;width:35%;"></div>
        </div>
      </div>
    `).join('');
  } else {
    levelsHtml = levels.map((lvl, index) => {
      const isFree = !lvl.unlock_type || lvl.unlock_type === 'free';
      const isCoin = lvl.unlock_type === 'coin';
      const isInvite = lvl.unlock_type === 'invite';
      
      const hasAccess = lvl.user_has_access || isPremiumActive();
      const locked = !hasAccess;
      
      let cardBg = '', cardBorder = '', cardShadow = '';
      if (isFree) {
        cardBg = 'linear-gradient(135deg, #0d2b12, #163d1a)';
        cardBorder = 'rgba(57,255,20,0.15)';
        cardShadow = '0 4px 16px rgba(57,255,20,0.08)';
      } else if (isCoin) {
        cardBg = 'linear-gradient(135deg, #2b1e00, #3d2d00)';
        cardBorder = 'rgba(255,215,0,0.2)';
        cardShadow = '0 4px 16px rgba(255,215,0,0.1)';
      } else if (lvl.unlock_type === 'premium') {
        cardBg = 'linear-gradient(135deg, #1a0d2e, #2d1a00)';
        cardBorder = 'rgba(255,140,0,0.25)';
        cardShadow = '0 4px 16px rgba(255,140,0,0.12)';
      } else if (isInvite) {
        cardBg = 'linear-gradient(135deg, #0a1628, #0f2040)';
        cardBorder = 'rgba(45,107,255,0.2)';
        cardShadow = '0 4px 16px rgba(45,107,255,0.1)';
      }

      let badgeHtml = '';
      if (isPremiumActive() && (lvl.unlock_type === 'premium')) {
        badgeHtml = Badge({ text: t('badge_open'), variant: 'premium' });
      } else if (isInvite) {
        if (locked) badgeHtml = Badge({ text: t('badge_invite').replace('{n}', String(lvl.unlock_cost || 5)), icon: '🤝', variant: 'invite' });
        else badgeHtml = Badge({ text: t('badge_open'), variant: 'free' });
      } else if (lvl.unlock_type === 'premium') {
        if (locked) badgeHtml = Badge({ text: t('badge_premium'), icon: '👑', variant: 'premium' });
        else badgeHtml = Badge({ text: t('badge_open'), variant: 'free' });
      } else if (isCoin) {
        if (locked) badgeHtml = Badge({ text: String(lvl.unlock_cost || 0), icon: '🪙', variant: 'coin' });
        else badgeHtml = Badge({ text: t('badge_open'), variant: 'free' });
      } else {
        badgeHtml = Badge({ text: t('badge_free'), variant: 'free' });
      }

      let levelAction = '';
      if (hasAccess) {
        levelAction = 'open-setup';
      } else if (isCoin) {
        levelAction = 'show-unlock';
      } else if (isInvite) {
        levelAction = 'goto-invite';
      } else {
        levelAction = 'goto-premium';
      }
      
      const iconDelay = -(index * 0.7) + 's';
      
      const rightIcon = locked
        ? `<span style="font-size:20px; opacity:0.6;">🔒</span>`
        : `<span style="font-size:18px; color:rgba(255,255,255,0.7);">▶</span>`;
      
      const shimmerDiv = (lvl.unlock_type === 'premium') ? `
        <div style="position:absolute;inset:0;pointer-events:none;overflow:hidden;border-radius:inherit;${locked ? ' opacity:0.5;' : ''}">
          <div style="position:absolute;inset:0;transform:translateX(-150%);animation:shimmer 2.5s ease-in-out infinite;
                     background:linear-gradient(90deg,transparent 0%,rgba(255,215,0,0.12) 50%,transparent 100%);">
          </div>
        </div>
      ` : '';

      return `
        <div
          data-level-action="${levelAction}"
          data-game-id="${sanitizeHtml(activeGameId)}"
          data-level-id="${sanitizeHtml(lvl.id)}"
          data-level-index="${index}"
          data-level-name="${sanitizeHtml(getLocalized(lvl.name))}"
          data-level-icon="${sanitizeHtml(lvl.icon)}"
          data-level-cost="${lvl.unlock_cost ?? 0}"
          style="
            display:flex; align-items:center; gap:14px;
            padding:14px 16px; border-radius:18px; cursor:pointer;
            position:relative; overflow:hidden;
            background:${cardBg};
            border:1px solid ${cardBorder};
            box-shadow:${cardShadow};
            transition:transform 0.15s ease, opacity 0.15s ease;
          "
          onpointerdown="this.style.transform='scale(0.97)'"
          onpointerup="this.style.transform='scale(1)'"
          onpointerleave="this.style.transform='scale(1)'"
        >
          ${shimmerDiv}
          
          <!-- Emoji слева -->
          <div style="
            font-size:36px; flex-shrink:0; width:52px; height:52px;
            display:flex; align-items:center; justify-content:center;
            animation:iconFloat 3s ease-in-out infinite;
            animation-delay:${iconDelay};
            ${locked ? 'opacity:0.5;' : ''}
          ">${lvl.icon}</div>
          
          <!-- Текст по центру -->
          <div style="flex:1; min-width:0;">
            <div style="${locked ? 'opacity:0.5;' : ''}">
              <div style="font-family:'Inter',sans-serif; font-size:15px; font-weight:800; color:#fff; line-height:1.2; margin-bottom:3px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">
                ${sanitizeHtml(getLocalized(lvl.name))}
              </div>
              <div style="font-size:11px; color:rgba(176,173,207,0.85); font-weight:600; line-height:1.3; margin-bottom:8px; overflow:hidden; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical;">
                ${sanitizeHtml(getLocalized(lvl.desc))}
              </div>
            </div>
            ${badgeHtml}
          </div>
          
          <!-- Иконка справа -->
          <div style="flex-shrink:0; width:32px; display:flex; align-items:center; justify-content:center; ${locked ? 'opacity:0.5;' : ''}">
            ${rightIcon}
          </div>
        </div>
      `;
    }).join('');
  }

  return `
    <div class="absolute inset-0 overflow-y-auto overflow-x-hidden pb-[96px] z-10 scrollbar-none bg-transparent opacity-100 pointer-events-auto transform translate-y-0" id="screen-games">
      
      <!-- Top Bar -->
      <div class="flex items-center justify-between p-[16px_20px_8px] relative z-[2]">
        <div class="font-display text-[22px] font-extrabold bg-grad-primary bg-clip-text text-transparent [-webkit-text-fill-color:transparent]">${t('levels_title')}</div>
        <div class="flex items-center gap-[6px]">
          ${CoinChip({ amount: state.user?.coins ?? 0, onClick: "nav-premium-coins" })}
          ${isPremiumActive() ? `<div class="flex items-center gap-[4px] bg-neon-gold/[0.15] border border-neon-gold/[0.3] rounded-pill py-[5px] px-[10px]">
            <span class="text-[13px]">👑</span>
            <span class="text-[11px] font-extrabold text-neon-gold">PRO</span>
          </div>` : ''}
        </div>
      </div>

      <!-- Game selector tabs -->
      <div class="mb-[16px]">
        <div class="flex gap-[8px] pl-[16px] pr-[32px] pb-[4px] overflow-x-auto scrollbar-none mb-[4px]">
          ${tabsHtml}
        </div>
      </div>

      <!-- Level flex list -->
      <div class="flex flex-col gap-[10px] px-[16px]">
        ${levelsHtml}
      </div>

      <div class="h-[24px]"></div>
    </div>
  `;
}
